import { NextRouter } from "next/router";

import { uniqBy } from "@s-libs/micro-dash";
import {
  GenerationGeniusAPIResponse,
  IGeniusPDF,
} from "@/types/models/GenerationGenius.interface";
import {
  TGeniusPanelName,
  TGeniusPanelOptionType,
} from "@/view/GeniusContent/GeniusActivityPanel/GeniusActivityPanel.constant";
import { formatGeniusActivityPanelContent } from "@/view/GeniusContent/utils";
import { GeniusAttachment } from "@/view/GeniusContent/GeniusContent.interface";

export const getClickHandler = ({
  router,
  contentData,
}: {
  router: NextRouter;
  contentData: GenerationGeniusAPIResponse;
}) => {
  return ({
    type,
    activityPanel,
    url,
    assignmentId,
  }: {
    type: TGeniusPanelOptionType;
    activityPanel?: TGeniusPanelName;
    assignmentId?: string | undefined;
    url?: string | undefined;
  }) => {
    if (type === "download") {
      downloadGeniusPDF({ contentData, activityPanel });
    } else if (type === "external-link" && url) {
      window.open(url, "_blank");
    } else if (activityPanel) {
      openGeniusActivityPanel({ router, activityPanel, assignmentId });
    }
  };
};

export const openGeniusActivityPanel = ({
  router,
  activityPanel,
  assignmentId,
}: {
  router: NextRouter;
  activityPanel: TGeniusPanelName;
  assignmentId?: string | undefined;
}) => {
  const href = {
    pathname: router.pathname,
    query: {
      ...router.query,
      activityPanel: activityPanel.toLowerCase(),
      ...(assignmentId && { assignmentId }),
    },
  };
  router.replace(href, undefined, { shallow: true });
};

export const downloadGeniusPDF = ({
  contentData,
  activityPanel,
}: {
  contentData: GenerationGeniusAPIResponse;
  activityPanel: TGeniusPanelName | undefined;
}) => {
  try {
    const activityType = activityPanel
      ?.toLowerCase()
      .replaceAll(" ", "_")
      .replace(/\(PDF\)/g, "")
      .trim();

    const pdfs = JSON.parse(contentData.pdfs) as IGeniusPDF[];
    const pdfData = pdfs.find((pdf) => pdf.type === activityType);
    const fileURL = pdfData?.url;

    // Create a link element to trigger the download
    if (fileURL) {
      const link = document.createElement("a");
      link.href = fileURL;
      link.download = `${pdfData.type}.pdf`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    console.error("Error downloading lesson plan PDF:", error);
    throw error;
  }
};

export const getBackButtonHandler = (router: NextRouter) => {
  return () => {
    const query = { ...router.query };
    delete query?.["activityPanel"];
    delete query?.["assignmentId"];
    void router.replace({ pathname: router.pathname, query }, undefined, {
      shallow: true,
    });
  };
};

export const getPdfResourceVisibility = (pdfResources: IGeniusPDF[]) => {
  const resourceSet = new Set(pdfResources.map((resource) => resource.type));

  return {
    hasReadingMaterial: resourceSet.has("reading_materials"),
    hasDiyPdfUrl: resourceSet.has("diy_activity_guide"),
    hasTeacherResources:
      resourceSet.has("teacher_guide") || resourceSet.has("lesson_plan"),
  };
};

export const getActivitiesFromContentData = ({
  contentData,
  isTeacher,
}: {
  contentData: GenerationGeniusAPIResponse;
  isTeacher: boolean;
}) => {
  const { vocabulary, discussionQuestions, pdfResources } =
    formatGeniusActivityPanelContent(contentData);

  const hasVocabulary = Boolean(vocabulary?.content?.length);

  const hasDiscussionQuestions = Boolean(discussionQuestions?.content?.length);

  const { hasDiyPdfUrl, hasTeacherResources, hasReadingMaterial } =
    getPdfResourceVisibility(pdfResources);

  const activities: {
    title: string;
    hidden: boolean;
    type: TGeniusPanelOptionType;
    activityPanel: TGeniusPanelName;
    url?: string | undefined;
  }[] = [
    {
      title: "Teacher resources",
      hidden: !hasTeacherResources || !isTeacher,
      type: "internal-panel-link",
      activityPanel: "teacher_resources",
    },
    {
      title: "Reading material (PDF)",
      hidden: !hasReadingMaterial,
      type: "external-link",
      activityPanel: "reading_materials",
      url: pdfResources.find((pdf) => pdf.type === "reading_materials")?.url,
    },
    {
      title: "Discussion questions",
      hidden: !hasDiscussionQuestions,
      type: "internal-panel-link",
      activityPanel: "discussion_questions",
    },
    {
      title: "Vocabulary",
      hidden: !hasVocabulary,
      type: "internal-panel-link",
      activityPanel: "vocabulary",
    },
    {
      title: "DIY activity guide (PDF)",
      hidden: !hasDiyPdfUrl,
      type: "external-link",
      activityPanel: "diy_activity_guide",
      url: pdfResources.find((pdf) => pdf.type === "diy_activity_guide")?.url,
    },
  ];

  return activities.filter((activity) => !activity.hidden);
};

export const getSortedFormativeAssessments = ({
  assessments,
  expectedNames,
}: {
  assessments: GeniusAttachment[];
  expectedNames: string[];
}) => {
  if (!assessments || assessments.length === 0) return [];

  const uniqueAssessments = uniqBy(
    assessments,
    (attachment) => attachment.data.name,
  );

  const attachmentMap = new Map(assessments.map((att) => [att.data.name, att]));

  // Assessments in expected order with undefined values filtered out
  const orderedAssessments = expectedNames
    .map((name) => attachmentMap.get(name))
    .filter((att): att is (typeof uniqueAssessments)[0] => att !== undefined);

  // Assessments not found in expectedNames are placed at the end
  const additionalAssessments = uniqueAssessments.filter(
    (att) => !expectedNames.includes(att.data.name),
  );

  return [...orderedAssessments, ...additionalAssessments];
};
